import { apiFetch, tokenStorage, type ApiResponse, API_BASE_URL } from "./api";
import type { OkResponse, ProfileResponse } from "./types";

export interface AuthResponse {
  accessToken: string;
  refreshToken: string;
  user: ProfileResponse;
}

// Register (профиль обязателен в одном запросе)
export interface RegisterRequest {
  authMethod: "email" | "phone";
  email?: string;
  loginPhone?: string;
  password: string;
  agreeToPrivacy: boolean;
  agreeToTerms: boolean;
  agreeToOffer: boolean;
  agreeToAge18: boolean;
  name: string;
  birthDate: string;
  birthTime?: string;
  birthCity?: string;
  gender: "male" | "female";
  avatarUrl: string;
  photos?: string[];
  bio?: string;
  interests?: string[];
  location?: string;
  /** Цель знакомств: по ней и полу сервер сразу выставляет, кого показывать в ленте (#1257). */
  relationshipGoal?: string;
  /** Регистрация по персональной ссылке-приглашению (/invite/:inviterId) — id пригласившего. */
  inviterId?: string;
}

/** Занятость email/телефона до создания аккаунта (шаг 1 степпера регистрации). */
export async function checkAvailability(
  data: { email?: string; loginPhone?: string },
): Promise<ApiResponse<{ emailTaken: boolean | null; phoneTaken: boolean | null }>> {
  return apiFetch<{ emailTaken: boolean | null; phoneTaken: boolean | null }>(
    "/api/auth/check-availability",
    { method: "POST", body: JSON.stringify(data) },
    { public: true },
  );
}

/**
 * Промежуточный ответ регистрации по email: аккаунт создан, но токенов ещё
 * нет — сессия появляется только после verifyRegistrationCode().
 */
export interface RegistrationChallenge {
  verificationRequired: true;
  /** Одноразовый идентификатор попытки; передаётся в verify/resend. */
  challengeId: string;
  emailHint: string;
  expiresInSeconds: number;
  resendAfterSeconds: number;
  /** Письмо с кодом реально принято SMTP. false — почта не настроена/отправка упала (#1271). */
  emailDelivered?: boolean;
  /** Только DEV-стенд: код возвращается в ответе, чтобы тестировать без почты. */
  devCode?: string;
}

/** Честное сообщение, если письмо с кодом не ушло; null — всё в порядке (или сервер не сообщил). */
export function registrationMailProblem(challenge: Pick<RegistrationChallenge, "emailDelivered">): string | null {
  return challenge.emailDelivered === false
    ? "Письмо с кодом не отправлено: почта на сервере не настроена или недоступна. Попробуйте позже."
    : null;
}

export type RegisterResult = AuthResponse | RegistrationChallenge;

export function isRegistrationChallenge(result: RegisterResult): result is RegistrationChallenge {
  return (result as RegistrationChallenge).verificationRequired === true;
}

export async function register(data: RegisterRequest): Promise<ApiResponse<RegisterResult>> {
  const response = await apiFetch<RegisterResult>("/api/auth/register", {
    method: "POST",
    body: JSON.stringify(data),
  }, { public: true });

  if (response.data && !isRegistrationChallenge(response.data)) {
    tokenStorage.setTokens(response.data.accessToken, response.data.refreshToken);
  }

  return response;
}

/** Шаг 2 регистрации по email: код из письма в обмен на токены. */
export async function verifyRegistrationCode(
  challengeId: string,
  code: string,
): Promise<ApiResponse<AuthResponse>> {
  const response = await apiFetch<AuthResponse>("/api/auth/register/verify", {
    method: "POST",
    body: JSON.stringify({ challengeId, code }),
  }, { public: true });

  if (response.data) {
    tokenStorage.setTokens(response.data.accessToken, response.data.refreshToken);
  }

  return response;
}

/** Прислать новый код подтверждения регистрации: сервер выдаёт новый challengeId. */
export async function resendRegistrationCode(
  challengeId: string,
): Promise<ApiResponse<RegistrationChallenge>> {
  return apiFetch<RegistrationChallenge>("/api/auth/register/resend", {
    method: "POST",
    body: JSON.stringify({ challengeId }),
  }, { public: true });
}

// Login: поле email — email или телефон (как вводит пользователь)
export interface LoginRequest {
  email: string;
  password: string;
}

/**
 * Промежуточный ответ входа: пароль принят, но нужен код с почты.
 * Токенов в нём нет — сессия появляется только после verifyLoginCode().
 */
export interface TwoFactorChallenge {
  twoFactorRequired: true;
  /** Одноразовый идентификатор попытки входа; передаётся в verify/resend. */
  challengeId: string;
  /** Замаскированный адрес для подписи «код отправлен на …». */
  emailHint: string;
  expiresInSeconds: number;
  resendAfterSeconds: number;
  /** Только DEV-стенд: код возвращается в ответе, чтобы тестировать без почты. */
  devCode?: string;
}

export type LoginResult = AuthResponse | TwoFactorChallenge;

export function isTwoFactorChallenge(result: LoginResult): result is TwoFactorChallenge {
  return (result as TwoFactorChallenge).twoFactorRequired === true;
}

/** Токены сохраняются только у полноценного ответа входа, не у челленджа второго фактора. */
function persistTokensIfAuthenticated(result: LoginResult | null): void {
  if (result && !isTwoFactorChallenge(result)) {
    tokenStorage.setTokens(result.accessToken, result.refreshToken);
  }
}

export async function login(data: LoginRequest): Promise<ApiResponse<LoginResult>> {
  const response = await apiFetch<LoginResult>("/api/auth/login", {
    method: "POST",
    body: JSON.stringify(data),
  }, { public: true });

  persistTokensIfAuthenticated(response.data);

  return response;
}

/** Шаг 2 входа: код из письма в обмен на токены. */
export async function verifyLoginCode(
  challengeId: string,
  code: string,
): Promise<ApiResponse<AuthResponse>> {
  const response = await apiFetch<AuthResponse>("/api/auth/login/verify", {
    method: "POST",
    body: JSON.stringify({ challengeId, code }),
  }, { public: true });

  if (response.data) {
    tokenStorage.setTokens(response.data.accessToken, response.data.refreshToken);
  }

  return response;
}

/** Прислать новый код: сервер выдаёт новый challengeId, старый код перестаёт работать. */
export async function resendLoginCode(
  challengeId: string,
): Promise<ApiResponse<TwoFactorChallenge>> {
  return apiFetch<TwoFactorChallenge>("/api/auth/login/resend", {
    method: "POST",
    body: JSON.stringify({ challengeId }),
  }, { public: true });
}

// Вход администратора отдельной ручки не имеет: админ логинится обычным
// login() выше, а доступ к /admin даёт роль в JWT (user.role ∈ admin/moderator/
// support). Здесь раньше жил loginByAdminCode() с общим кодом-паролем — код
// уезжал в собранный бандл и работал бессрочно; удалён 2026-08-22.


// Forgot password — отправка ссылки на email (бэкенд: /api/auth/forgot-password)
export interface ForgotPasswordResponse {
  ok: boolean;
  /** Только в DEV-окружении — ссылка/токен, чтобы можно было протестировать без email. */
  devResetLink?: string;
  devResetToken?: string;
}

export async function forgotPassword(
  email: string
): Promise<ApiResponse<ForgotPasswordResponse>> {
  return apiFetch<ForgotPasswordResponse>(
    "/api/auth/forgot-password",
    {
      method: "POST",
      body: JSON.stringify({ email: email.trim().toLowerCase() }),
    },
    { public: true }
  );
}

// Reset password — установка нового пароля по токену из письма.
export async function resetPassword(
  token: string,
  password: string
): Promise<ApiResponse<OkResponse>> {
  return apiFetch<OkResponse>(
    "/api/auth/reset-password",
    {
      method: "POST",
      body: JSON.stringify({ token, password }),
    },
    { public: true }
  );
}

// Change password — смена пароля авторизованным пользователем.
export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
}

/**
 * Смена пароля: сервер отзывает ВСЕ старые refresh-токены (сессии на других
 * устройствах умирают) и выдаёт свежую пару для текущей. Сохраняем её сразу —
 * иначе после отзыва старого refresh-токена разлогинит и это устройство.
 */
export async function changePassword(
  data: ChangePasswordRequest
): Promise<ApiResponse<{ accessToken: string; refreshToken: string }>> {
  const response = await apiFetch<{ accessToken: string; refreshToken: string }>(
    "/api/auth/change-password",
    {
      method: "POST",
      body: JSON.stringify(data),
    }
  );

  if (response.data) {
    tokenStorage.setTokens(response.data.accessToken, response.data.refreshToken);
  }

  return response;
}

/** Отзыв refresh-токена на сервере; локальные токены чистит вызывающий код. */
export async function logout(): Promise<ApiResponse<OkResponse>> {
  const refreshToken = tokenStorage.getRefreshToken();

  const response = await apiFetch<OkResponse>("/api/auth/logout", {
    method: "POST",
    body: JSON.stringify({ refreshToken }),
  });

  tokenStorage.clearTokens();
  return response;
}

// ── Yandex OAuth ────────────────────────────────────────────────────────────────

/**
 * Yandex OAuth credentials — задаются при сборке через Vite env.
 *
 * VITE_YANDEX_REDIRECT_URI — необязательное переопределение. По умолчанию
 * redirectUri берётся от текущего origin (`${window.location.origin}/app`),
 * так что dev.forruss.ru и forruss.ru получают каждый свой адрес без
 * build-args в CI. Оба адреса обязаны быть зарегистрированы как Redirect URI
 * в приложении на oauth.yandex.ru: https://forruss.ru/app и
 * https://dev.forruss.ru/app.
 */
export const YANDEX_OAUTH = {
  clientId: (import.meta.env.VITE_YANDEX_CLIENT_ID as string) || "73341712022047bd820f5de59a6358ac",
  get redirectUri(): string {
    return (import.meta.env.VITE_YANDEX_REDIRECT_URI as string) || `${window.location.origin}/app`;
  },
};

/** Client-side redirect to Yandex: the user authenticates on Yandex and comes back to /app?code=… */
export function buildYandexAuthorizeUrl(state?: unknown): string {
  const params = new URLSearchParams({
    response_type: "code",
    client_id: YANDEX_OAUTH.clientId,
    redirect_uri: YANDEX_OAUTH.redirectUri,
  });
  // `state` есть только у привязки из профиля (#1296): по нему возврат отличают от обычного входа.
  // typeof: функцию могли отдать обработчиком onClick, тогда сюда придёт MouseEvent, а не state.
  if (typeof state === "string" && state) params.set("state", state);
  return `https://oauth.yandex.ru/authorize?${params.toString()}`;
}

export function redirectToYandexOAuth(state?: string): void {
  window.location.href = buildYandexAuthorizeUrl(state);
}

/** Yandex login response (same shape as a regular AuthResponse). */
export interface YandexLoginRequest {
  code: string;
  redirectUri: string;
}

/** Exchange a Yandex OAuth code for tokens. */
export async function yandexLogin(
  data: YandexLoginRequest,
): Promise<ApiResponse<AuthResponse>> {
  const response = await apiFetch<AuthResponse>("/api/auth/yandex", {
    method: "POST",
    body: JSON.stringify(data),
  }, { public: true });

  if (response.data) {
    tokenStorage.setTokens(response.data.accessToken, response.data.refreshToken);
  }

  return response;
}

// Messenger OAuth
export function redirectToMessengerOAuth(): void {
  window.location.href = `${API_BASE_URL}/api/auth/messenger`;
}

// ── VK ID OAuth ────────────────────────────────────────────────────────────────

/** VK OAuth credentials — defined at build time via Vite env vars. */
export const VK_OAUTH = {
  appId: (import.meta.env.VITE_VK_APP_ID as string) || "54747482",
  redirectUri: (import.meta.env.VITE_VK_REDIRECT_URI as string) || "https://forruss.ru/app",
};

/** VK login result passed after VKID SDK exchange. */
export interface VkLoginRequest {
  vkId: string;
  accessToken: string;
  email?: string;
  firstName: string;
  lastName: string;
  avatar?: string;
  phone?: string;
}

/** Exchange VK ID data for our JWT tokens. */
export async function vkLogin(
  data: VkLoginRequest,
): Promise<ApiResponse<AuthResponse>> {
  const response = await apiFetch<AuthResponse>("/api/auth/vk", {
    method: "POST",
    body: JSON.stringify(data),
  }, { public: true });

  if (response.data) {
    tokenStorage.setTokens(response.data.accessToken, response.data.refreshToken);
  }

  return response;
}